import React, { Component, useState } from 'react';
import { bool, func, object, shape, string, number } from 'prop-types';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { ValidationError, ExpandingTextarea } from '../../components';
import IconCollection from '../IconCollection/IconCollection';
import Button from '../Button/Button';

import css from './FieldTextInput.module.css';
import Tooltip from '../ToolTip/ToolTip';

const CONTENT_MAX_LENGTH = 5000;

const FieldTextInputComponent = props => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const {
    rootClassName,
    className,
    inputRootClass,
    customErrorText,
    id,
    label,
    input,
    meta,
    onUnmount,
    isUncontrolled,
    inputRef,
    hideErrorMessage,
    helperText,
    srOnly,
    iconLeft,
    iconRight,
    buttonRight,
    isStepper,
    stepperMin = 0,
    stepperMax = 100,
    stepperStep = 1,
    iconLeftText,
    required,
    variant,
    toolTipText,
    ...rest
  } = props;

  if (label && !id) {
    throw new Error('id required when a label is given');
  }

  const { valid, invalid, touched, submitFailed, error } = meta;
  const isTextarea = input.type === 'textarea';
  const isPassword = input.type === 'password';
  const isDisabled = input.disabled || rest.disabled;

  const errorText = customErrorText || error;

  // Error message and input error styles are only shown if the
  // field has been touched and the validation has failed.
  const hasError = !!customErrorText || !!((touched || submitFailed) && invalid && error);

  const fieldMeta = { touched: hasError, error: errorText };

  // Textarea doesn't need type.
  const { type, ...inputWithoutType } = input;
  const effectiveType = isPassword && isPasswordVisible ? 'text' : type;
  // Uncontrolled input uses defaultValue instead of value.
  const { value: defaultValue, ...inputWithoutValue } = input;
  // Use inputRef if it is passed as prop.
  // For textarea (ExpandingTextarea), use innerRef prop, for input use ref
  const refMaybe = inputRef ? { ref: inputRef } : {};
  const innerRefMaybe = inputRef ? { innerRef: inputRef } : {};

  const inputClasses =
    inputRootClass ||
    classNames(css.input, {
      [css.inputSuccess]: valid,
      [css.inputError]: hasError,
      [css.textarea]: isTextarea,
    });
  const maxLength = CONTENT_MAX_LENGTH;
  const inputProps = isTextarea
    ? {
      className: inputClasses,
      id,
      rows: 1,
      maxLength,
      ...innerRefMaybe,
      ...inputWithoutType,
      ...rest,
    }
    : isUncontrolled
      ? {
        className: inputClasses,
        id,
        type: effectiveType,
        defaultValue,
        ...refMaybe,
        ...inputWithoutValue,
        ...rest,
      }
      : { className: inputClasses, id, type: effectiveType, ...refMaybe, ...inputWithoutType, ...rest };

  const classes = classNames(rootClassName || css.root, className);

  // Stepper logic
  const [stepperValue, setStepperValue] = React.useState(Number(input.value) || 0);
  React.useEffect(() => {
    if (isStepper && input.value !== stepperValue) {
      setStepperValue(Number(input.value) || 0);
    }
  }, [input.value, isStepper]);
  const handleStepper = dir => {
    let newValue = stepperValue + (dir === 'inc' ? stepperStep : -stepperStep);
    if (newValue < stepperMin) newValue = stepperMin;
    if (newValue > stepperMax) newValue = stepperMax;
    setStepperValue(newValue);
    input.onChange(newValue);
  };

  // Auto icons/buttons based on variant or type
  let autoIconLeft = null;
  let autoIconRight = null;
  let autoButtonRight = null;
  let autoIconLeftText = null;

  if (variant === 'email') {
    autoIconLeft = <IconCollection name="email" width={18} height={18} />;
    autoIconRight = <IconCollection name="info_icon" width={16} height={16} />;
  }
  if (variant === 'externalUrl') {
    autoIconLeft = <IconCollection name="externalUrl" width={18} height={18} />;
    autoIconLeftText = <span>https://</span>;
  }
  if (variant === 'website') {
    autoIconRight = <IconCollection name="info_icon" width={16} height={16} />;
    autoButtonRight = <Button className={css.copyButton}>
      <IconCollection name="copy" width={16} height={16} />
      Copy</Button>;
  }

  // Password toggle icon
  if (isPassword) {
    autoIconRight = (
      <button
        type="button"
        className={css.passwordToggle}
        aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
        onClick={() => setIsPasswordVisible(v => !v)}
      >
        {isPasswordVisible ? (
          <i className='ri-eye-line'></i>
        ) : (
          <i className='ri-eye-off-line'></i>
        )}
      </button>
    );
  }

  // Use passed-in iconLeft/iconRight/buttonRight if provided, otherwise use auto
  const iconLeftToUse = iconLeft ? iconLeft : autoIconLeft;
  const iconRightToUse = iconRight ? iconRight : autoIconRight;
  const buttonRightToUse = buttonRight ? buttonRight : autoButtonRight;
  const iconLeftTextToUse = iconLeftText ? iconLeftText : autoIconLeftText;

  // Input rendering
  const [showTooltip, setShowTooltip] = React.useState(false);
  let inputElement;
  if (isStepper) {
    inputElement = (
      <div className={css.inputWrapper} id='inputWrapper'>
        <button type="button" className={css.stepperButton} onClick={() => handleStepper('dec')} disabled={isDisabled || stepperValue <= stepperMin}>-</button>
        <input
          className={css.stepperInput}
          type="number"
          min={stepperMin}
          max={stepperMax}
          step={stepperStep}
          value={stepperValue}
          disabled={isDisabled}
          readOnly
        />
        <button type="button" className={css.stepperButton} onClick={() => handleStepper('inc')} disabled={isDisabled || stepperValue >= stepperMax}>+</button>
      </div>
    );
  } else {
    inputElement = (
      <div style={{ display: "flex" }}>
        {iconLeftTextToUse ? <span className={css.inputIconLeftText}>{iconLeftTextToUse}</span> : null}
        <div className={classNames(css.inputWrapper, {
          [css.inputError]: hasError,
          [css.inputDisabled]: isDisabled,
          [css.inputWithButton]: buttonRightToUse,
          [css.inputWithIconLeftText]: iconLeftTextToUse,
        })} id='inputWrapper'>
          {iconLeftToUse ? <span className={css.inputIcon}>{iconLeftToUse}</span> : null}
          {isTextarea ? (
            <ExpandingTextarea {...inputProps} disabled={isDisabled} />
          ) : (
            <input {...inputProps} disabled={isDisabled} />
          )}
          {iconRightToUse && helperText ? (
            <span
              className={css.inputIconRight}
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              style={{ position: 'relative' }}
            >
              {iconRightToUse}
              {showTooltip && (
                <span className={css.helperTooltip}>{helperText}</span>
              )}
            </span>
          ) : (
            iconRightToUse ? <span className={css.inputIconRight}>{iconRightToUse}</span> : null
          )}
        </div>
        {buttonRightToUse ? <span className={css.inputButton}>{buttonRightToUse}</span> : null}
      </div>
    );
  }

  return (
    <div className={classes}>
      {label ? (
        <label className={srOnly ? css.srOnly : null} htmlFor={id}>
          {label}
          {required && <span className={css.requiredText}>&nbsp;*</span>}
          {toolTipText && <Tooltip size="lg" content={toolTipText}>
            &nbsp;  <i className='ri-question-line' />
          </Tooltip>}
        </label>
      ) : null}
      {inputElement}
      {!!helperText?.trim() && !hasError && !iconRightToUse ? (
        <div className={css.helperText}>{helperText}</div>
      ) : null}
      {hasError && !hideErrorMessage ? <div className={css.errorText}>{errorText}</div> : null}
    </div>
  );
};

FieldTextInputComponent.defaultProps = {
  rootClassName: null,
  className: null,
  inputRootClass: null,
  onUnmount: null,
  customErrorText: null,
  id: null,
  label: null,
  isUncontrolled: false,
  inputRef: null,
  iconLeft: null,
  iconRight: null,
  buttonRight: null,
  isStepper: false,
  stepperMin: 0,
  stepperMax: 100,
  stepperStep: 1,
  variant: undefined,
};

FieldTextInputComponent.propTypes = {
  rootClassName: string,
  className: string,
  inputRootClass: string,

  onUnmount: func,

  // Error message that can be manually passed to input field,
  // overrides default validation message
  customErrorText: string,

  // Label is optional, but if it is given, an id is also required so
  // the label can reference the input in the `for` attribute
  id: string,
  label: string,

  // Uncontrolled input uses defaultValue prop, but doesn't pass value from form to the field.
  // https://reactjs.org/docs/uncontrolled-components.html#default-values
  isUncontrolled: bool,
  // a ref object passed for input element.
  inputRef: object,

  // Generated by final-form's Field component
  input: shape({
    onChange: func.isRequired,
    // Either 'textarea' or something that is passed to the input element
    type: string.isRequired,
  }).isRequired,
  meta: object.isRequired,
  iconLeft: object,
  iconRight: object,
  buttonRight: object,
  isStepper: bool,
  stepperMin: number,
  stepperMax: number,
  stepperStep: number,
  variant: string,
};

class FieldTextInput extends Component {
  componentWillUnmount() {
    // Unmounting happens too late if it is done inside Field component
    // (Then Form has already registered its (new) fields and
    // changing the value without corresponding field is prohibited in Final Form
    if (this.props.onUnmount) {
      this.props.onUnmount();
    }
  }

  render() {
    return <Field component={FieldTextInputComponent} {...this.props} />;
  }
}

export default FieldTextInput;
